import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { FadeUp } from "@/components/Reveal";
import { api } from "@/lib/api";
import { DEFAULT_PRODUCT_IMAGE } from "@/lib/productImage";

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const [availability, setAvailability] = useState({});

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      const map = {};
      data.forEach((p) => { map[p.id] = { available: p.in_stock !== false, stock: p.stock ?? 0 }; });
      setAvailability(map);
    }).catch(() => {});
  }, []);

  const isUnavailable = (id) => Boolean(availability[id] && !availability[id].available);
  const stockOf = (id) => availability[id]?.stock;
  const overStock = (i) => stockOf(i.product_id) != null && i.quantity > stockOf(i.product_id);
  const hasUnavailable = items.some((i) => isUnavailable(i.product_id));
  const hasOverStock = items.some(overStock);
  const checkoutBlocked = hasUnavailable || hasOverStock;

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="cart-page">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <FadeUp>
          <p className="label text-accent mb-6">/ Cart</p>
          <h1 className="font-display text-5xl md:text-7xl font-light leading-[0.9] tracking-tight">Your selection</h1>
        </FadeUp>

        {items.length === 0 ? (
          <div className="mt-20 flex flex-col items-start gap-6" data-testid="empty-cart">
            <ShoppingBag size={40} strokeWidth={1} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Your cart is empty.</p>
            <Link to="/shop" className="label inline-flex items-center gap-2 border-b border-foreground pb-1 hover:text-accent hover:border-accent transition-colors" data-testid="empty-cart-cta">
              Browse the collection <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        ) : (
          <div className="mt-14 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-8 border-t border-border" data-testid="cart-items">
              {items.map((item) => {
                const lineStock = stockOf(item.product_id);
                const atMax = lineStock != null && item.quantity >= lineStock;
                return (
                <div key={item.product_id} className="flex gap-5 py-6 border-b border-border" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-24 h-32 shrink-0 overflow-hidden bg-secondary border border-border">
                    <img src={DEFAULT_PRODUCT_IMAGE} alt={item.name} className="h-full w-full object-contain bg-white p-2" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-display text-2xl leading-tight">{item.name}</h3>
                        <p className="mt-1 font-mono text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
                        {isUnavailable(item.product_id) && (
                          <p className="mt-2 label text-destructive" data-testid={`sold-out-${item.product_id}`}>Sold out — remove to continue</p>
                        )}
                        {!isUnavailable(item.product_id) && overStock(item) && (
                          <p className="mt-2 label text-destructive" data-testid={`over-stock-${item.product_id}`}>Only {lineStock} left — reduce quantity</p>
                        )}
                      </div>
                      <button onClick={() => removeItem(item.product_id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-${item.product_id}`} aria-label="Remove item">
                        <X size={18} strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-border">
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="h-10 w-10 flex items-center justify-center hover:bg-secondary transition-colors" data-testid={`decrease-${item.product_id}`} aria-label="Decrease">
                          <Minus size={14} strokeWidth={1.5} />
                        </button>
                        <span className="w-10 text-center font-mono text-sm" data-testid={`qty-${item.product_id}`}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={atMax} className="h-10 w-10 flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`increase-${item.product_id}`} aria-label="Increase">
                          <Plus size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                      <span className="font-mono text-sm">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            <div className="md:col-span-4">
              <div className="border border-border p-8 sticky top-28" data-testid="cart-summary">
                <p className="label text-muted-foreground mb-6">Summary</p>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mb-6 pb-6 border-b border-border">
                  <span className="text-muted-foreground">Shipping{subtotal >= 200 ? "" : " (flat)"}</span>
                  <span className="font-mono">{subtotal >= 200 ? "FREE" : "$13.00"}</span>
                </div>
                {subtotal > 0 && subtotal < 200 && (
                  <p className="text-xs text-accent mb-4" data-testid="free-ship-hint">
                    Add ${(200 - subtotal).toFixed(2)} more for free shipping.
                  </p>
                )}
                <div className="flex justify-between mb-8">
                  <span className="label">Total</span>
                  <span className="font-mono text-lg">${(subtotal + (subtotal >= 200 ? 0 : 13)).toFixed(2)}</span>
                </div>
                {hasUnavailable && (
                  <p className="mb-4 text-xs text-destructive" data-testid="cart-sold-out-warning">
                    Remove sold out items above to continue to checkout.
                  </p>
                )}
                {!hasUnavailable && hasOverStock && (
                  <p className="mb-4 text-xs text-destructive" data-testid="cart-over-stock-warning">
                    Some quantities exceed available stock — reduce them to continue.
                  </p>
                )}
                <button onClick={() => navigate("/checkout")} disabled={checkoutBlocked} className={`w-full h-14 label flex items-center justify-center gap-2 transition-colors ${checkoutBlocked ? "bg-secondary text-muted-foreground cursor-not-allowed" : "bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"}`} data-testid="checkout-btn">
                  Checkout <ArrowRight size={16} strokeWidth={1.5} />
                </button>
                <p className="mt-4 text-xs text-muted-foreground text-center">Demo checkout — no payment required.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
